/**
 * Hook d'authentification basé sur React Query.
 *
 * La session est portée par un cookie HttpOnly côté serveur — le frontend
 * ne stocke jamais le token. On dérive l'état "connecté" du succès ou non
 * de l'appel GET /me.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import { getMe, type UserProfile } from "@/api/me";

const ME_KEY = ["me"] as const;

export function useMe() {
  return useQuery<UserProfile, ApiError>({
    queryKey: ME_KEY,
    queryFn: getMe,
    retry: (failureCount, error) => {
      // Pas de retry sur 401/403 (non authentifié)
      if (error.status === 401 || error.status === 403) return false;
      return failureCount < 1;
    },
    staleTime: 60_000,
  });
}

export function useAuthStatus() {
  const { data, error, isPending } = useMe();
  // `isLoading` ne doit être vrai QUE tant qu'on n'a pas la première réponse.
  // Une fois qu'on a soit un user (data), soit un 401 (error), l'état d'auth
  // est connu — même si TanStack Query re-fetch en arrière-plan (StrictMode,
  // refocus, etc.), on ne doit pas re-passer en "loading" sinon les routes
  // protégées par RequireGuest / RequireAuth flashent en page blanche.
  const hasResolved = data !== undefined || error !== null;
  const isLoading = !hasResolved && isPending;
  const isAuthenticated = !!data;
  const needsEmailVerification = error?.status === 403 && error.code === "EMAIL_NOT_VERIFIED";
  return { isAuthenticated, isLoading, user: data, needsEmailVerification };
}

export function useInvalidateAuth() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ME_KEY });
}

export function useClearAuth() {
  const qc = useQueryClient();
  return () => qc.setQueryData(ME_KEY, null);
}
